import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dispatchAPI, crmJobsAPI } from '../lib/api';
import { predictiveRestockAPI } from '../lib/v2Api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Loader2, ChevronLeft, ChevronRight, Truck, Wrench, ShieldCheck,
  AlertTriangle, CircleAlert, Package, Clock, RefreshCw, Save, X, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { PrepareTruckDrawer } from '../components/PrepareTruckDrawer';

function formatTime(dt) {
  if (!dt) return '';
  return new Date(dt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDateHeader(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_CONFIG = {
  ready:         { label: 'Ready',        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: ShieldCheck, tip: 'Vehicle has all required parts for this job.' },
  low_stock:     { label: 'Low Stock',    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', icon: AlertTriangle, tip: 'Parts available but stock will be low after use.' },
  missing_items: { label: 'Missing Parts',color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: CircleAlert, tip: 'Vehicle is missing parts needed for this job.' },
  no_vehicle:    { label: 'No Vehicle',   color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', icon: Truck, tip: 'No vehicle assigned to this job.' },
  no_kit:        { label: 'No Kit',       color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', icon: Package, tip: 'No Job Kit assigned — cannot check readiness.' },
};

function ReadinessBadge({ status, label }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.no_kit;
  const Icon = cfg.icon;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${cfg.color}`} data-testid={`status-badge-${status}`}>
            <Icon className="w-3 h-3" /> {label || cfg.label}
          </span>
        </TooltipTrigger>
        <TooltipContent>{cfg.tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function TruckPrep() {
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readinessMap, setReadinessMap] = useState({});
  const [readinessLoading, setReadinessLoading] = useState(false);

  // Filters
  const [showReady, setShowReady] = useState(true);
  const [showMissing, setShowMissing] = useState(true);
  const [showLowStock, setShowLowStock] = useState(true);
  const [showNoKit, setShowNoKit] = useState(true);
  const [techFilter, setTechFilter] = useState('all');

  // Drawers
  const [prepareTruckJobId, setPrepareTruckJobId] = useState(null);
  const [bulkRestockOpen, setBulkRestockOpen] = useState(false);
  const [bulkRestockRows, setBulkRestockRows] = useState([]);
  const [bulkRestockSaving, setBulkRestockSaving] = useState(false);

  // Fetch events for selected date
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatchAPI.listEvents({ start_date: selectedDate, end_date: selectedDate });
      setEvents(res.data.events || []);
    } catch {
      toast.error('Failed to load dispatch events');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Batch-fetch readiness for all jobs
  useEffect(() => {
    if (!events.length) { setReadinessMap({}); return; }
    const jobIds = [...new Set(events.map(e => e.job_id).filter(Boolean))];
    setReadinessLoading(true);
    const fetchAll = async () => {
      const map = {};
      await Promise.allSettled(
        jobIds.map(async (jid) => {
          try {
            const res = await crmJobsAPI.readiness(jid);
            map[jid] = res.data;
          } catch { /* skip */ }
        })
      );
      setReadinessMap(map);
      setReadinessLoading(false);
    };
    fetchAll();
  }, [events]);

  const shiftDate = (days) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  // Unique techs for filter
  const techOptions = useMemo(() => {
    const map = {};
    events.forEach(e => {
      if (e.assigned_user_id && e.assigned_user_name) {
        map[e.assigned_user_id] = e.assigned_user_name;
      }
    });
    return Object.entries(map).map(([id, name]) => ({ id, name }));
  }, [events]);

  // Apply filters
  const filteredEvents = useMemo(() => {
    return events.filter(evt => {
      const r = readinessMap[evt.job_id];
      const status = r?.status || 'no_kit';

      if (status === 'ready' && !showReady) return false;
      if (status === 'missing_items' && !showMissing) return false;
      if (status === 'low_stock' && !showLowStock) return false;
      if ((status === 'no_kit' || status === 'no_vehicle') && !showNoKit) return false;
      if (techFilter !== 'all' && evt.assigned_user_id !== techFilter) return false;

      return true;
    });
  }, [events, readinessMap, showReady, showMissing, showLowStock, showNoKit, techFilter]);

  // Stats
  const stats = useMemo(() => {
    let ready = 0, missing = 0, lowStock = 0, noKit = 0;
    events.forEach(evt => {
      const status = readinessMap[evt.job_id]?.status || 'no_kit';
      if (status === 'ready') ready++;
      else if (status === 'missing_items') missing++;
      else if (status === 'low_stock') lowStock++;
      else noKit++;
    });
    return { ready, missing, lowStock, noKit, total: events.length };
  }, [events, readinessMap]);

  // Bulk restock — aggregate missing items across all vehicles
  const openBulkRestock = () => {
    const itemMap = {};
    Object.values(readinessMap).forEach(r => {
      if (r.status !== 'missing_items' || !r.vehicle_id) return;
      r.items.forEach(item => {
        if (item.status === 'missing' || item.status === 'partial') {
          const key = `${r.vehicle_id}::${item.item_id}`;
          const needed = Math.max(0, item.quantity_required - item.quantity_available);
          if (!itemMap[key]) {
            itemMap[key] = {
              item_id: item.item_id,
              item_name: item.item_name,
              sku: item.sku,
              vehicle_id: r.vehicle_id,
              vehicle_name: r.vehicle_name,
              needed: 0,
              editQty: 0,
            };
          }
          itemMap[key].needed += needed;
          itemMap[key].editQty += needed;
        }
      });
    });
    const rows = Object.values(itemMap).filter(r => r.needed > 0);
    if (!rows.length) { toast.info('No missing items to restock'); return; }
    setBulkRestockRows(rows);
    setBulkRestockOpen(true);
  };

  const updateBulkQty = (key, value) => {
    setBulkRestockRows(prev => prev.map((r, i) =>
      i === key ? { ...r, editQty: Math.max(0, parseInt(value) || 0) } : r
    ));
  };

  const removeBulkRow = (key) => {
    setBulkRestockRows(prev => prev.filter((_, i) => i !== key));
  };

  const handleBulkSave = async () => {
    const valid = bulkRestockRows.filter(r => r.editQty > 0);
    if (!valid.length) { toast.error('No items to save'); return; }
    setBulkRestockSaving(true);
    try {
      const res = await predictiveRestockAPI.savePlan({
        name: `Daily Truck Prep — ${formatDateHeader(selectedDate)}`,
        items: valid.map(r => ({
          item_id: r.item_id,
          source_type: 'vehicle',
          source_id: r.vehicle_id,
          suggested_qty: r.editQty,
        })),
      });
      toast.success(`Restock list created with ${res.data.total_items} items`);
      setBulkRestockOpen(false);
      navigate('/replenishment');
    } catch {
      toast.error('Failed to save bulk restock plan');
    } finally {
      setBulkRestockSaving(false);
    }
  };

  // Quick single-job restock
  const handleQuickRestock = (jobId) => {
    const r = readinessMap[jobId];
    if (!r || !r.vehicle_id) return;
    const missing = r.items.filter(i => i.status === 'missing' || i.status === 'partial');
    if (!missing.length) return;
    const rows = missing.map(i => ({
      item_id: i.item_id,
      item_name: i.item_name,
      sku: i.sku,
      vehicle_id: r.vehicle_id,
      vehicle_name: r.vehicle_name,
      needed: Math.max(0, i.quantity_required - i.quantity_available),
      editQty: Math.max(0, i.quantity_required - i.quantity_available),
    }));
    setBulkRestockRows(rows);
    setBulkRestockOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="truck-prep-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="truck-prep-title">Daily Truck Prep</h1>
            <p className="text-sm text-muted-foreground mt-1">Prepare all vehicles for today's scheduled jobs.</p>
          </div>
          {canEdit && stats.missing > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button onClick={openBulkRestock} data-testid="restock-all-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Restock All Missing
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Aggregate all missing parts across vehicles and create a single restock plan.</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Date Navigation + Filters */}
        <div className="flex flex-wrap items-center gap-3" data-testid="truck-prep-filters">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => shiftDate(-1)} data-testid="prev-day"><ChevronLeft className="w-4 h-4" /></Button>
              </TooltipTrigger>
              <TooltipContent>Previous day</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40" data-testid="date-picker" />
              </TooltipTrigger>
              <TooltipContent>Select a date to view scheduled jobs.</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span className="text-sm font-medium hidden sm:inline">{formatDateHeader(selectedDate)}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => shiftDate(1)} data-testid="next-day"><ChevronRight className="w-4 h-4" /></Button>
              </TooltipTrigger>
              <TooltipContent>Next day</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} data-testid="today-btn">Today</Button>
              </TooltipTrigger>
              <TooltipContent>Jump to today's schedule.</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="h-6 w-px bg-border hidden sm:block" />

          {/* Status filters */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant={showReady ? 'default' : 'outline'} size="sm" onClick={() => setShowReady(!showReady)} data-testid="filter-ready" className="text-xs h-7">
                  <Check className="w-3 h-3 mr-1" /> Ready
                </Button>
              </TooltipTrigger>
              <TooltipContent>Toggle visibility of jobs with all parts ready.</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant={showMissing ? 'default' : 'outline'} size="sm" onClick={() => setShowMissing(!showMissing)} data-testid="filter-missing" className="text-xs h-7">
                  <CircleAlert className="w-3 h-3 mr-1" /> Missing
                </Button>
              </TooltipTrigger>
              <TooltipContent>Toggle visibility of jobs with missing parts.</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant={showLowStock ? 'default' : 'outline'} size="sm" onClick={() => setShowLowStock(!showLowStock)} data-testid="filter-lowstock" className="text-xs h-7">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Low Stock
                </Button>
              </TooltipTrigger>
              <TooltipContent>Toggle visibility of jobs with low stock warnings.</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Tech filter */}
          {techOptions.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Select value={techFilter} onValueChange={setTechFilter}>
                      <SelectTrigger className="h-7 text-xs w-[140px]" data-testid="filter-tech">
                        <SelectValue placeholder="All Techs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Technicians</SelectItem>
                        {techOptions.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Filter by assigned technician.</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Summary Cards */}
        {!loading && events.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="truck-prep-stats">
            <Card className="border-green-200 dark:border-green-900/30">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                <div>
                  <div className="text-lg font-bold text-green-600" data-testid="stat-ready">{stats.ready}</div>
                  <div className="text-xs text-muted-foreground">Ready</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 dark:border-red-900/30">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <CircleAlert className="w-5 h-5 text-red-600" />
                <div>
                  <div className="text-lg font-bold text-red-600" data-testid="stat-missing">{stats.missing}</div>
                  <div className="text-xs text-muted-foreground">Missing Parts</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 dark:border-amber-900/30">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <div className="text-lg font-bold text-amber-600" data-testid="stat-lowstock">{stats.lowStock}</div>
                  <div className="text-xs text-muted-foreground">Low Stock</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <Package className="w-5 h-5 text-muted-foreground" />
                <div>
                  <div className="text-lg font-bold" data-testid="stat-total">{stats.total}</div>
                  <div className="text-xs text-muted-foreground">Total Jobs</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : events.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Truck className="w-12 h-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No jobs scheduled</h3>
              <p className="text-sm text-muted-foreground mb-4">No dispatch events found for {formatDateHeader(selectedDate)}.</p>
              <Button variant="outline" onClick={() => navigate('/dispatch')}>Go to Dispatch</Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Time</TooltipTrigger>
                        <TooltipContent>Scheduled start time of the job.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                      <TableHead>
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Customer</TooltipTrigger>
                        <TooltipContent>Customer assigned to this job.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                      <TableHead>
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Job</TooltipTrigger>
                        <TooltipContent>Job number and name.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                      <TableHead>
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Technician</TooltipTrigger>
                        <TooltipContent>Assigned technician for this job.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                      <TableHead>
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Vehicle</TooltipTrigger>
                        <TooltipContent>Vehicle assigned to the technician.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                      <TableHead>
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Readiness</TooltipTrigger>
                        <TooltipContent>Whether the vehicle has all required parts for this job.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                      <TableHead className="text-right w-24">
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Missing</TooltipTrigger>
                        <TooltipContent>Number of parts that are missing or insufficient on the vehicle.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                      <TableHead className="w-32">
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Actions</TooltipTrigger>
                        <TooltipContent>Quick actions: open Prepare Truck drawer or create a restock plan.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No jobs match the current filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEvents.map((evt) => {
                        const r = readinessMap[evt.job_id];
                        const status = r?.status || 'no_kit';
                        const missingCount = r?.missing_count || 0;
                        return (
                          <TableRow key={evt.id} data-testid={`truck-prep-row-${evt.id}`}>
                            <TableCell className="text-sm">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3 text-muted-foreground" />
                                {formatTime(evt.start_at)}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">{evt.customer_name || '—'}</TableCell>
                            <TableCell>
                              <button className="text-sm font-medium text-primary hover:underline" onClick={() => navigate(`/jobs/${evt.job_id}`)} data-testid={`prep-job-link-${evt.id}`}>
                                {evt.job_number}
                              </button>
                              {evt.job_name && <div className="text-xs text-muted-foreground truncate max-w-[160px]">{evt.job_name}</div>}
                            </TableCell>
                            <TableCell className="text-sm">{evt.assigned_user_name || <span className="text-muted-foreground italic">Unassigned</span>}</TableCell>
                            <TableCell className="text-sm">{r?.vehicle_name || <span className="text-muted-foreground italic">—</span>}</TableCell>
                            <TableCell>
                              {readinessLoading ? (
                                <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                              ) : (
                                <ReadinessBadge status={status} label={r?.status_label} />
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {missingCount > 0 ? (
                                <span className="text-sm font-medium text-red-600" data-testid={`missing-count-${evt.id}`}>{missingCount}</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">0</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-7" onClick={() => setPrepareTruckJobId(evt.job_id)} data-testid={`prep-action-${evt.id}`}>
                                        <Truck className="w-3.5 h-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Open Prepare Truck drawer for this job.</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {status === 'missing_items' && r?.vehicle_id && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-7" onClick={() => handleQuickRestock(evt.job_id)} data-testid={`restock-action-${evt.id}`}>
                                          <RefreshCw className="w-3.5 h-3.5" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Create a restock plan for missing parts on this vehicle.</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Prepare Truck Drawer */}
        <PrepareTruckDrawer
          open={!!prepareTruckJobId}
          onClose={() => setPrepareTruckJobId(null)}
          jobId={prepareTruckJobId}
          onRestocked={() => { setPrepareTruckJobId(null); fetchEvents(); }}
        />

        {/* Bulk Restock Drawer */}
        <AppDrawer
          open={bulkRestockOpen}
          onClose={() => setBulkRestockOpen(false)}
          title="Bulk Restock Plan"
          description={`Aggregated missing parts for ${formatDateHeader(selectedDate)}`}
        >
          <div className="space-y-4" data-testid="bulk-restock-drawer">
            <p className="text-sm text-muted-foreground">
              Review and adjust quantities for all missing parts across vehicles. This creates a single replenishment list.
            </p>

            {bulkRestockRows.length > 0 ? (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead className="text-right w-20">
                        <TooltipProvider><Tooltip><TooltipTrigger className="cursor-help underline decoration-dotted">Need</TooltipTrigger>
                        <TooltipContent>Quantity needed to meet job requirements.</TooltipContent></Tooltip></TooltipProvider>
                      </TableHead>
                      <TableHead className="text-right w-28">Restock Qty</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkRestockRows.map((row, idx) => (
                      <TableRow key={idx} data-testid={`bulk-restock-row-${idx}`}>
                        <TableCell>
                          <div className="text-sm font-medium">{row.item_name}</div>
                          {row.sku && <div className="text-xs text-muted-foreground">{row.sku}</div>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{row.vehicle_name}</TableCell>
                        <TableCell className="text-right text-sm">{row.needed}</TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min={0}
                            value={row.editQty}
                            onChange={(e) => updateBulkQty(idx, e.target.value)}
                            className="w-20 h-8 text-right text-sm ml-auto"
                            data-testid={`bulk-restock-qty-${idx}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeBulkRow(idx)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No items in the restock plan.</p>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setBulkRestockOpen(false)}>Cancel</Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="flex-1"
                      onClick={handleBulkSave}
                      disabled={bulkRestockSaving || !bulkRestockRows.some(r => r.editQty > 0)}
                      data-testid="bulk-save-restock-btn"
                    >
                      {bulkRestockSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                      Save Restock List
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Save this bulk restock plan for warehouse-to-vehicle transfers.</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </AppDrawer>
      </div>
    </Layout>
  );
}
